import { type CruiseCabinRate, type CruiseDetail } from '@utazzo/shared';
import { AlertTriangle, Check, Circle } from 'lucide-react';
import React, { useMemo } from 'react';

/**
 * Compact setup checklist for the cruise editor sidebar - the cruise
 * analogue of `HotelSetupCard`. Tells the operator what still blocks this
 * cruise from being bookable (details, cabins, rates, photos, approval),
 * with a completion bar.
 */

interface CruiseSetupCardProps {
  cruise: CruiseDetail;
  rates: CruiseCabinRate[];
  ratesLoading: boolean;
  /** Jump to the Cabins & rates tab for cabin/rate items. */
  onGoToCabins: () => void;
}

interface ChecklistItem {
  key: string;
  label: string;
  state: 'done' | 'todo' | 'warn';
  onClick?: () => void;
}

const fmtPrice = (amount: number, currencyCode: string | null): string => {
  const code = currencyCode ?? 'INR';
  const prefix = code === 'INR' ? '₹' : `${code} `;
  return `${prefix}${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

export const CruiseSetupCard = ({
  cruise,
  rates,
  ratesLoading,
  onGoToCabins,
}: CruiseSetupCardProps): React.JSX.Element => {
  const photoCount = Array.isArray(cruise.photos) ? cruise.photos.length : 0;

  const { checklist, stats } = useMemo(() => {
    const cabinsWithRates = new Set(
      rates.map((r) => r.cruiseCabinId).filter((id): id is string => Boolean(id)),
    );
    const cabinsMissingRates = cruise.cabins.filter((c) => !cabinsWithRates.has(c.id));

    const activePrices = rates
      .filter((r) => r.isActive)
      .map((r) => r.adultPrice || r.fixedCabinCost || 0)
      .filter((n) => n > 0);
    const minPrice = activePrices.length ? Math.min(...activePrices) : null;

    const items: ChecklistItem[] = [
      {
        key: 'details',
        label: cruise.cityId ? 'Basic details' : 'Basic details (homeport missing)',
        state: cruise.name && cruise.cityId ? 'done' : 'todo',
      },
      {
        key: 'cabins',
        label:
          cruise.cabins.length > 0
            ? `${cruise.cabins.length} cabin${cruise.cabins.length === 1 ? '' : 's'} added`
            : 'Add cabins',
        state: cruise.cabins.length > 0 ? 'done' : 'todo',
        onClick: onGoToCabins,
      },
      {
        key: 'rates',
        label:
          cruise.cabins.length === 0
            ? 'Add rates'
            : cabinsMissingRates.length === 0
              ? 'All cabins have rates'
              : `${cabinsMissingRates.length} cabin${cabinsMissingRates.length === 1 ? '' : 's'} without rates`,
        state:
          ratesLoading || cruise.cabins.length === 0
            ? 'todo'
            : cabinsMissingRates.length === 0
              ? 'done'
              : 'warn',
        onClick: onGoToCabins,
      },
      {
        key: 'photos',
        label: photoCount > 0 ? `${photoCount} photo${photoCount === 1 ? '' : 's'}` : 'Add photos',
        state: photoCount > 0 ? 'done' : 'todo',
      },
      {
        key: 'approval',
        label: cruise.isApprovedByAdmin ? 'Approved' : 'Awaiting approval',
        state: cruise.isApprovedByAdmin ? 'done' : 'todo',
      },
    ];

    return {
      checklist: items,
      stats: {
        cabins: cruise.cabins.length,
        rates: rates.length,
        minPrice,
        currencyCode: rates[0]?.currencyCode ?? null,
      },
    };
  }, [cruise, rates, ratesLoading, photoCount, onGoToCabins]);

  const doneCount = checklist.filter((c) => c.state === 'done').length;

  return (
    <section className="rounded-lg border border-zinc-200 bg-white">
      <header className="flex items-center gap-2 border-b border-zinc-100 px-4 py-3">
        <h3 className="text-sm font-semibold tracking-tight text-zinc-900">Setup</h3>
        <span className="text-xs text-zinc-500 tabular-nums">
          {doneCount}/{checklist.length}
        </span>
        <span className="flex-1" />
        <div className="h-1.5 w-16 overflow-hidden rounded-full bg-zinc-100">
          <div
            className="h-full rounded-full bg-emerald-500 transition-[width] duration-300"
            style={{ width: `${(doneCount / checklist.length) * 100}%` }}
          />
        </div>
      </header>
      <div className="space-y-0.5 px-2 py-2">
        {checklist.map((item) => {
          const icon =
            item.state === 'done' ? (
              <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden />
            ) : item.state === 'warn' ? (
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden />
            ) : (
              <Circle className="h-3.5 w-3.5 shrink-0 text-zinc-300" aria-hidden />
            );
          const text = (
            <span
              className={
                'text-xs ' +
                (item.state === 'done'
                  ? 'text-zinc-500'
                  : item.state === 'warn'
                    ? 'font-medium text-amber-700'
                    : 'text-zinc-700')
              }
            >
              {item.label}
            </span>
          );
          return item.onClick && item.state !== 'done' ? (
            <button
              key={item.key}
              type="button"
              onClick={item.onClick}
              className="flex w-full items-center gap-2 rounded px-2 py-1 text-left transition-colors hover:bg-zinc-50"
            >
              {icon}
              {text}
            </button>
          ) : (
            <span key={item.key} className="flex items-center gap-2 px-2 py-1">
              {icon}
              {text}
            </span>
          );
        })}
      </div>
      {/* Stats footer */}
      <footer className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-zinc-100 px-4 py-2.5 text-xs text-zinc-500">
        <span>
          <span className="font-medium text-zinc-700 tabular-nums">{stats.cabins}</span> cabin
          {stats.cabins === 1 ? '' : 's'}
        </span>
        <span>
          <span className="font-medium text-zinc-700 tabular-nums">{stats.rates}</span> rate
          {stats.rates === 1 ? '' : 's'}
        </span>
        {stats.minPrice !== null && (
          <span>
            from{' '}
            <span className="font-medium text-zinc-700 tabular-nums">
              {fmtPrice(stats.minPrice, stats.currencyCode)}
            </span>
          </span>
        )}
      </footer>
    </section>
  );
};
